/**
 * Deterministic colour for the cellar table's STIL (style/region) chip —
 * mirrors Vinify's colored region tags (inv1_dashboard.webp / Shelf view):
 * a rotating palette keyed by a hash of the label so the same
 * region/country always gets the same colour, without maintaining an
 * explicit region→colour map.
 */
const PALETTE: Array<{ bg: string; fg: string }> = [
  { bg: '#F7E3B0', fg: '#6B4E12' }, // gold
  { bg: '#F3C6CE', fg: '#7A2334' }, // rose
  { bg: '#D8C8F2', fg: '#4A2E7A' }, // lavender
  { bg: '#C7E3D4', fg: '#1F5C3F' }, // sage
  { bg: '#F5D3B3', fg: '#7A4111' }, // amber
  { bg: '#C9DCF2', fg: '#1E4573' }, // sky
  { bg: '#E6D2C3', fg: '#5C3A20' }, // taupe
  { bg: '#F0C9DC', fg: '#7A1F52' }, // pink
];

/** Mirrors backend services/wine-list-settings.ts WINE_LIST_STYLE_LABELS. */
const WINE_STYLE_LABELS: Record<string, string> = {
  sparkling: 'Musserende',
  white: 'Hvitvin',
  rose: 'Rosévin',
  red: 'Rødvin',
  sweet_fortified: 'Dessert / Sterkvin',
};

/**
 * "Stil" column label. For wine-category bottles with a resolved catalog
 * wine style (see Bottle.catalogWineStyle — set server-side by
 * resolveWineStyle in wine-list-service.ts), shows the actual style
 * (Rødvin/Hvitvin/Musserende/...), which is what the column header promises.
 * Otherwise falls back to region/country/category, same as before — a
 * client-side port of the full metadata+keyword heuristic isn't done here to
 * avoid duplicating (and drifting from) the backend's resolveWineStyle.
 */
export function styleChipLabel(bottle: { region?: string; country?: string; category: string; catalogWineStyle?: string }): string {
  if (bottle.category === 'wine' && bottle.catalogWineStyle && WINE_STYLE_LABELS[bottle.catalogWineStyle]) {
    return WINE_STYLE_LABELS[bottle.catalogWineStyle];
  }
  return bottle.region || bottle.country || bottle.category;
}

export function styleChipColor(label: string): { bg: string; fg: string } {
  let hash = 0;
  for (let i = 0; i < label.length; i++) {
    hash = (hash * 31 + label.charCodeAt(i)) >>> 0;
  }
  return PALETTE[hash % PALETTE.length];
}
